import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp, TrendingDown, Users, Minus, Instagram, BarChart3,
  Loader2, ArrowUp, ArrowDown, Eye,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const V2 = '/api/v2'

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

interface AccountOverview {
  accountId: string
  igUserId: string
  username: string
  followers: number
  follows: number
  mediaCount: number
  snapshotAt: string
  growth24h: number | null
  growth7d: number | null
}

interface OverviewData {
  totals: {
    followers: number
    accounts: number
    growth24h: number
    growth7d: number
  }
  accounts: AccountOverview[]
}

interface HistorySnapshot {
  followers: number
  follows: number
  mediaCount: number
  date: string
}

interface HistoryData {
  username: string
  snapshots: HistorySnapshot[]
}

function useIgOverview() {
  return useQuery<OverviewData>({
    queryKey: ['ig-analytics-overview'],
    queryFn: async () => {
      const res = await fetch(`${V2}/ig-analytics/overview`, { headers: authHeaders() })
      if (!res.ok) throw new Error('Failed to fetch')
      return res.json()
    },
  })
}

function useIgHistory(accountId: string | null, days = 30) {
  return useQuery<HistoryData>({
    queryKey: ['ig-analytics-history', accountId, days],
    queryFn: async () => {
      const res = await fetch(`${V2}/ig-analytics/${accountId}/history?days=${days}`, { headers: authHeaders() })
      if (!res.ok) throw new Error('Failed to fetch')
      return res.json()
    },
    enabled: !!accountId,
  })
}

const fmtNum = (n: number) => new Intl.NumberFormat('pt-BR').format(n)

function GrowthBadge({ value, label }: { value: number | null; label: string }) {
  if (value === null) return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Minus className="h-3 w-3" />
      <span>{label}: --</span>
    </div>
  )

  const positive = value > 0
  const neutral = value === 0

  return (
    <div className={cn(
      'flex items-center gap-1.5 text-xs font-medium',
      positive && 'text-emerald-500',
      value < 0 && 'text-red-500',
      neutral && 'text-muted-foreground',
    )}>
      {positive ? <ArrowUp className="h-3 w-3" /> : value < 0 ? <ArrowDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
      <span>{label}: {positive ? '+' : ''}{fmtNum(value)}</span>
    </div>
  )
}

function TotalCard({ label, value, icon: Icon, growth, growthLabel, className }: {
  label: string
  value: string | number
  icon: React.ElementType
  growth?: number
  growthLabel?: string
  className?: string
}) {
  return (
    <Card className={cn('relative overflow-hidden p-5', className)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold mt-1">{typeof value === 'number' ? fmtNum(value) : value}</p>
          {growth !== undefined && growthLabel && (
            <div className={cn(
              'flex items-center gap-1 text-xs font-medium mt-2',
              growth > 0 && 'text-emerald-500',
              growth < 0 && 'text-red-500',
              growth === 0 && 'text-muted-foreground',
            )}>
              {growth > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : growth < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
              <span>{growth > 0 ? '+' : ''}{fmtNum(growth)} {growthLabel}</span>
            </div>
          )}
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      </div>
    </Card>
  )
}

function MiniChart({ snapshots }: { snapshots: HistorySnapshot[] }) {
  if (snapshots.length < 2) return null

  const values = snapshots.map(s => s.followers)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1

  const width = 240
  const height = 60
  const padding = 4

  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * (width - padding * 2)
    const y = height - padding - ((v - min) / range) * (height - padding * 2)
    return `${x},${y}`
  })

  const areaPoints = [
    `${padding},${height - padding}`,
    ...points,
    `${width - padding},${height - padding}`,
  ]

  const growing = values[values.length - 1] >= values[0]

  return (
    <svg width={width} height={height} className="mt-2">
      <defs>
        <linearGradient id={`grad-${growing ? 'up' : 'down'}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={growing ? '#10b981' : '#ef4444'} stopOpacity="0.2" />
          <stop offset="100%" stopColor={growing ? '#10b981' : '#ef4444'} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints.join(' ')}
        fill={`url(#grad-${growing ? 'up' : 'down'})`}
      />
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={growing ? '#10b981' : '#ef4444'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function AccountCard({ account, isSelected, onSelect }: {
  account: AccountOverview
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <Card
      className={cn(
        'p-4 cursor-pointer transition-all',
        isSelected && 'ring-2 ring-primary',
      )}
      onClick={onSelect}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white text-sm font-bold shrink-0">
          {account.username.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">@{account.username}</p>
          <p className="text-lg font-bold mt-0.5">{fmtNum(account.followers)} <span className="text-xs font-normal text-muted-foreground">seguidores</span></p>
          <div className="flex flex-wrap gap-3 mt-2">
            <GrowthBadge value={account.growth24h} label="24h" />
            <GrowthBadge value={account.growth7d} label="7d" />
          </div>
          <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
            <span>{fmtNum(account.follows)} seguindo</span>
            <span>{fmtNum(account.mediaCount)} posts</span>
          </div>
        </div>
      </div>
    </Card>
  )
}

function HistoryPanel({ accountId, username }: { accountId: string; username: string }) {
  const [days, setDays] = useState(30)
  const { data, isLoading } = useIgHistory(accountId, days)

  if (isLoading) return (
    <Card className="p-6">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Carregando historico...</span>
      </div>
    </Card>
  )

  const snapshots = data?.snapshots ?? []

  if (snapshots.length === 0) return (
    <Card className="p-6 text-center text-muted-foreground">
      <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
      <p className="text-sm">Sem dados historicos para @{username}</p>
      <p className="text-xs mt-1">Dados serao coletados a cada 4 horas</p>
    </Card>
  )

  const first = snapshots[0]
  const last = snapshots[snapshots.length - 1]
  const totalGrowth = last.followers - first.followers

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold">@{username}</h3>
          <p className="text-xs text-muted-foreground">
            {snapshots.length} coletas
            {' / '}
            {totalGrowth >= 0 ? '+' : ''}{fmtNum(totalGrowth)} seguidores no periodo
          </p>
        </div>
        <div className="flex gap-1">
          {[7, 14, 30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                days === d ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground',
              )}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <MiniChart snapshots={snapshots} />

      <div className="mt-4 max-h-[300px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-1.5 font-medium">Data</th>
              <th className="text-right py-1.5 font-medium">Seguidores</th>
              <th className="text-right py-1.5 font-medium">Seguindo</th>
              <th className="text-right py-1.5 font-medium">Posts</th>
              <th className="text-right py-1.5 font-medium">Variacao</th>
            </tr>
          </thead>
          <tbody>
            {[...snapshots].reverse().map((snap, i, arr) => {
              const prev = arr[i + 1]
              const diff = prev ? snap.followers - prev.followers : null
              return (
                <tr key={snap.date} className="border-b border-border/50">
                  <td className="py-1.5 text-muted-foreground">
                    {new Date(snap.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="text-right py-1.5 font-medium">{fmtNum(snap.followers)}</td>
                  <td className="text-right py-1.5">{fmtNum(snap.follows)}</td>
                  <td className="text-right py-1.5">{fmtNum(snap.mediaCount)}</td>
                  <td className={cn(
                    'text-right py-1.5 font-medium',
                    diff !== null && diff > 0 && 'text-emerald-500',
                    diff !== null && diff < 0 && 'text-red-500',
                  )}>
                    {diff === null ? '--' : diff > 0 ? `+${diff}` : diff}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

export function IgGrowth() {
  const { data, isLoading } = useIgOverview()
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)

  const selectedAccount = data?.accounts.find(a => a.accountId === selectedAccountId)

  if (isLoading) return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <Instagram className="h-6 w-6" />
          Crescimento Instagram
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Acompanhe o crescimento dos seus perfis</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}
      </div>
    </div>
  )

  const totals = data?.totals
  const accounts = data?.accounts ?? []

  if (accounts.length === 0) return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <Instagram className="h-6 w-6" />
          Crescimento Instagram
        </h1>
      </div>
      <Card className="p-12 text-center">
        <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
        <p className="text-lg font-medium text-muted-foreground">Sem dados ainda</p>
        <p className="text-sm text-muted-foreground/70 mt-1">Dados serao coletados automaticamente a cada 4 horas</p>
      </Card>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <Instagram className="h-6 w-6" />
          Crescimento Instagram
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Acompanhe o crescimento dos seus {accounts.length} perfis
        </p>
      </div>

      {/* Totals */}
      {totals && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <TotalCard
            label="Total Seguidores"
            value={totals.followers}
            icon={Users}
            growth={totals.growth24h}
            growthLabel="em 24h"
          />
          <TotalCard
            label="Crescimento 24h"
            value={totals.growth24h >= 0 ? `+${fmtNum(totals.growth24h)}` : fmtNum(totals.growth24h)}
            icon={totals.growth24h >= 0 ? TrendingUp : TrendingDown}
          />
          <TotalCard
            label="Crescimento 7d"
            value={totals.growth7d >= 0 ? `+${fmtNum(totals.growth7d)}` : fmtNum(totals.growth7d)}
            icon={totals.growth7d >= 0 ? TrendingUp : TrendingDown}
          />
          <TotalCard
            label="Perfis Monitorados"
            value={totals.accounts}
            icon={Instagram}
          />
        </div>
      )}

      {/* Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map(account => (
          <AccountCard
            key={account.accountId}
            account={account}
            isSelected={selectedAccountId === account.accountId}
            onSelect={() => setSelectedAccountId(
              selectedAccountId === account.accountId ? null : account.accountId
            )}
          />
        ))}
      </div>

      {/* History Panel */}
      {selectedAccount && (
        <HistoryPanel
          accountId={selectedAccount.accountId}
          username={selectedAccount.username}
        />
      )}
    </div>
  )
}
